import { nevisignFetch } from "@nevisign/reader-core";
import { faceCaptureFormData, type FaceCapture } from "../face-capture";
import {
  loadEsignLocal,
  saveEsignLocal,
  type ESignLocalCache,
} from "./local-store";

export type ESignPricing = {
  identityVerificationToman: number;
  signatureInfrastructureToman: number;
  rootCertificateToman: number;
};

export type ESignIdentityStep =
  | "idle"
  | "collect"
  | "otp_sent"
  | "otp_verified"
  | "civil_verified"
  | "face_matched"
  | "complete";

export type ESignProfile = {
  ok: true;
  userId: string;
  termsAcceptedAt: string | null;
  identityVerified: boolean;
  identityVerifiedAt: string | null;
  hasRootCertificate: boolean;
  rootCertificateIssuedAt: string | null;
  province: string | null;
  city: string | null;
  address: string | null;
  fingerprintEnabled: boolean;
  patternEnabled: boolean;
  hasUnlockPassword: boolean;
  hasPattern: boolean;
  hasSignatureSvg: boolean;
  signatureSvg: string | null;
  onboardingComplete: boolean;
  identityStep: ESignIdentityStep;
  /** National code the server verified; goes into the CSR subject (serialNumber). */
  verifiedNationalId?: string | null;
  /** Sentence read aloud in the identity video (speech recognition compares against it). */
  kycVideoLine?: string;
  pricing: ESignPricing;
  lastPaymentAmount: number | null;
  lastPaymentIncludedRootCert: boolean;
  debugCode?: string;
  paymentOk?: boolean;
};

async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text.trim()) {
    throw new Error("پاسخ خالی از سرور");
  }
  let data: T & { error?: unknown; message?: unknown };
  try {
    data = JSON.parse(text) as typeof data;
  } catch {
    throw new Error("پاسخ سرور قابل خواندن نبود");
  }
  if (!res.ok) {
    const nested =
      data &&
      typeof data === "object" &&
      data.error &&
      typeof data.error === "object"
        ? (data.error as { message?: unknown }).message
        : undefined;
    const msg =
      (typeof nested === "string" && nested) ||
      (typeof data.error === "string" && data.error) ||
      (typeof data.message === "string" && data.message) ||
      `خطای سرور (${res.status})`;
    throw new Error(msg);
  }
  return data;
}

function cacheFromProfile(profile: ESignProfile, prev?: ESignLocalCache | null): ESignLocalCache {
  return {
    userId: profile.userId,
    identityVerified: profile.identityVerified,
    hasRootCertificate: profile.hasRootCertificate,
    onboardingComplete: profile.onboardingComplete,
    fingerprintEnabled: profile.fingerprintEnabled,
    patternEnabled: profile.patternEnabled,
    unlockPasswordHash: prev?.unlockPasswordHash ?? null,
    patternHash: prev?.patternHash ?? null,
    signatureSvg: profile.signatureSvg ?? prev?.signatureSvg ?? null,
    province: profile.province,
    city: profile.city,
    address: profile.address,
    bannerDismissedAt: prev?.bannerDismissedAt ?? null,
  };
}

export function syncEsignCache(
  profile: ESignProfile,
  extras?: Partial<
    Pick<
      ESignLocalCache,
      "unlockPasswordHash" | "patternHash" | "bannerDismissedAt" | "signatureSvg"
    >
  >,
): ESignLocalCache {
  const prev = loadEsignLocal(profile.userId);
  const next = {
    ...cacheFromProfile(profile, prev),
    ...extras,
  };
  saveEsignLocal(next);
  return next;
}

export async function fetchEsignProfile(apiBase: string): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/profile", { cache: "no-store" });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile);
  return profile;
}

export async function acceptEsignTerms(apiBase: string): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/terms/accept", { method: "POST" });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile);
  return profile;
}

export async function completeEsignPayment(
  apiBase: string,
  input: { success: boolean; includeRootCertificate: boolean },
): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/payment/complete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile);
  return profile;
}

export async function startEsignIdentity(
  apiBase: string,
  input: { nationalId: string; phone: string },
): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/identity/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile);
  return profile;
}

export async function verifyEsignOtp(
  apiBase: string,
  code: string,
): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/identity/verify-otp", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile);
  return profile;
}

export async function submitEsignBirthDate(
  apiBase: string,
  birthDate: string,
  cardSerial: string,
): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/identity/birth-date", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ birthDate, cardSerial }),
  });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile);
  return profile;
}

export async function uploadEsignFaceMatch(
  apiBase: string,
  capture: FaceCapture,
): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/identity/face-match", {
    method: "POST",
    body: faceCaptureFormData(capture),
  });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile);
  return profile;
}

export async function completeEsignIdentity(apiBase: string): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/identity/complete", {
    method: "POST",
  });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile);
  return profile;
}

export async function saveEsignAddress(
  apiBase: string,
  input: { province: string; city: string; address: string },
): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/address", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile);
  return profile;
}

export async function saveEsignUnlockPrefs(
  apiBase: string,
  input: {
    unlockPasswordHash: string;
    fingerprintEnabled?: boolean;
    patternEnabled?: boolean;
    patternHash?: string | null;
  },
): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/unlock-prefs", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile, {
    unlockPasswordHash: input.unlockPasswordHash,
    patternHash: input.patternHash ?? null,
  });
  return profile;
}

export async function verifyEsignUnlock(
  apiBase: string,
  input: { unlockPasswordHash?: string; patternHash?: string },
): Promise<{ ok: true; unlocked: boolean }> {
  const res = await nevisignFetch(apiBase, "/e-sign/unlock/verify", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export async function saveEsignSignatureSvg(
  apiBase: string,
  signatureSvg: string,
): Promise<ESignProfile> {
  const res = await nevisignFetch(apiBase, "/e-sign/signature", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ signatureSvg }),
  });
  const profile = await readJson<ESignProfile>(res);
  syncEsignCache(profile, { signatureSvg });
  return profile;
}

export function formatToman(n: number): string {
  return n.toLocaleString("fa-IR");
}
